import React from 'react';

const KebabContact = () => {
  const FloatingElement = ({ children, delay = 0, duration = 40, startX = 0 }) => (
    <div
      className="absolute pointer-events-none opacity-10 animate-pulse"
      style={{
        left: `${startX}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    >
      {children}
    </div>
  );

  const FoodIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-red-600">
      <path
        d="M12 2C13.1 2 14 2.9 14 4V8C14 9.1 13.1 10 12 10S10 9.1 10 8V4C10 2.9 10.9 2 12 2ZM21 9V7L15 3.5V6.5L21 9ZM3 9L9 6.5V3.5L3 7V9ZM12 12C15.86 12 19 10.21 19 8V10C19 12.21 15.86 14 12 14S5 12.21 5 10V8C5 10.21 8.14 12 12 12Z"
        fill="currentColor"
        opacity="0.3"
      />
    </svg>
  );

  const contactMethods = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: 'Jetzt Anrufen',
      info: '0241 91991617',
      subtext: 'Für Bestellungen & Reservierungen',
      action: () => window.open('tel:+4924191991617'),
      highlight: true
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Uns Besuchen',
      info: 'Löhergraben 29-31',
      subtext: '52064 Aachen',
      action: () => window.open('https://maps.google.com/?q=Meister+Döner+Aachen', '_blank')
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Öffnungszeiten',
      info: 'Mo-Fr: 11:00-23:00',
      subtext: 'Sa: 11:00-23:00, So: 12:00-23:00',
      action: null
    }
  ];

  const quickOrder = [
    { name: 'Döner Normal', price: '6,00 €' },
    { name: 'Döner Dürüm', price: '7,00 €' },
    { name: 'Döner Teller', price: '10,00 €' },
    { name: 'Lahmacun', price: '6,50 €' },
    { name: 'Falafel Tasche', price: '5,00 €' },
    { name: 'Hähnchen Schnitzel', price: '9,00 €' }
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-zinc-950 via-red-950/10 to-zinc-950 overflow-hidden py-20">
      {/* Floating Background Elements */}
      <FloatingElement delay={0} duration={50} startX={10}>
        <FoodIcon />
      </FloatingElement>
      <FloatingElement delay={20} duration={60} startX={85}>
        <FoodIcon />
      </FloatingElement>
      <FloatingElement delay={40} duration={45} startX={30}>
        <FoodIcon />
      </FloatingElement>

      <div className="relative z-10 max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-6xl md:text-8xl font-bold text-red-600 mb-8 tracking-tight leading-none">
            <span className="block">BESTELLEN &</span>
            <span className="block bg-gradient-to-r from-red-600 via-red-500 to-red-600 bg-clip-text text-transparent">KONTAKT</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed">
            Bereit für authentischen Geschmack? Rufe einfach an und bestelle deine Lieblingsgerichte!
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {contactMethods.map((method, index) => (
            <div 
              key={index}
              className={`text-center p-8 bg-gradient-to-b from-red-900/20 to-zinc-900/30 border backdrop-blur-sm rounded-lg transition-all duration-300 hover:transform hover:-translate-y-2 ${
                method.highlight 
                  ? 'border-red-600/50 hover:border-red-500 cursor-pointer' 
                  : 'border-red-700/30 hover:border-red-600/50'
              } ${method.action ? 'cursor-pointer' : ''}`}
              onClick={method.action}
            >
              <div className={`mb-6 flex justify-center ${method.highlight ? 'text-red-500' : 'text-red-600'}`}>
                {method.icon}
              </div>
              <h3 className={`text-2xl font-bold mb-4 tracking-wide ${method.highlight ? 'text-red-400' : 'text-red-500'}`}>
                {method.title}
              </h3>
              <p className={`font-bold text-lg mb-2 ${method.highlight ? 'text-white' : 'text-gray-200'}`}>
                {method.info}
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                {method.subtext}
              </p>
              {method.highlight && (
                <div className="mt-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 rounded-full border border-red-600/40">
                    <span className="animate-pulse w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-red-400 text-sm font-medium">Jetzt verfügbar</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Menu Overview */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Popular Items */}
          <div className="bg-gradient-to-b from-red-900/20 to-zinc-900/30 border border-red-700/30 backdrop-blur-sm rounded-lg p-8">
            <h3 className="text-3xl font-bold text-red-500 mb-6 text-center">Unsere Bestseller</h3>
            <div className="grid grid-cols-1 gap-4">
              {quickOrder.map((item, index) => (
                <div 
                  key={index}
                  className="flex justify-between items-center p-4 bg-zinc-800/50 rounded border border-red-700/20 hover:bg-red-900/20 transition-colors"
                >
                  <span className="text-gray-200 font-medium">{item.name}</span>
                  <span className="text-red-400 font-bold text-lg">{item.price}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-red-900/20 rounded-lg border border-red-700/40">
              <p className="text-red-300 text-center font-medium">
                📞 Einfach anrufen und bestellen!
              </p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-gradient-to-b from-red-900/20 to-zinc-900/30 border border-red-700/30 backdrop-blur-sm rounded-lg p-8">
            <h3 className="text-3xl font-bold text-red-500 mb-6 text-center">Kontakt & Service</h3>
            <div className="space-y-6">
              <div className="text-center">
                <h4 className="text-xl font-bold text-red-400 mb-4">📞 Bestellung per Telefon</h4>
                <div className="text-gray-300 space-y-2">
                  <p className="text-2xl font-bold text-white">0241 91991617</p>
                  <p>Mo-Fr: 11:00 - 23:00</p>
                  <p>Sa: 11:00 - 23:00</p>
                  <p>So: 12:00 - 23:00</p>
                </div>
              </div>
              
              <div className="border-t border-red-700/30 pt-6">
                <h4 className="text-lg font-bold text-red-400 mb-4 text-center">Service-Optionen</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">🏃‍♂️</span>
                    <span>Abholung - Bereit in 15-20 Min</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">🚚</span>
                    <span>Lieferung - 2€ (ab 15€ Mindestbestellwert)</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">🪑</span>
                    <span>Vor Ort essen - Gemütliche Atmosphäre</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">💳</span>
                    <span>Bar & Karte - Alle Zahlungsarten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">🌱</span>
                    <span>Vegane Gerichte verfügbar</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">📶</span>
                    <span>WLAN für Gäste</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <span className="text-red-400">🐕</span>
                    <span>Hunde in Innenräumen erlaubt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="inline-block p-8 bg-gradient-to-r from-red-900/30 to-red-800/20 border border-red-700/40 backdrop-blur-sm rounded-lg">
            <h3 className="text-3xl md:text-4xl font-bold text-red-400 mb-4">Hunger? Dann ruf jetzt an!</h3>
            <p className="text-gray-300 font-light text-lg mb-8 max-w-2xl mx-auto">
              Unser Team nimmt gerne deine Bestellung entgegen und bereitet alles frisch für dich zu.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="relative px-12 py-4 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold text-xl tracking-wider rounded-lg transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-lg hover:shadow-red-600/20 group overflow-hidden"
                onClick={() => window.open('tel:+4924191991617')}
              >
                <span className="relative z-10 flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  0241 91991617
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-zinc-800 to-zinc-700 transform scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100"></div>
              </button>
              
              <button 
                className="relative px-8 py-4 border-2 border-red-600 text-red-400 font-bold tracking-wider transition-all duration-300 overflow-hidden group cursor-pointer rounded-lg"
                onClick={() => window.open('https://maps.google.com/?q=Meister+Döner+Aachen', '_blank')}
              >
                <span className="relative z-10 transition-colors duration-300 group-hover:text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  ZU UNS KOMMEN
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 transform scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Side Text */}
      <div className="hidden lg:block absolute left-8 top-1/2 transform -translate-y-1/2 -rotate-90 origin-left">
        <span className="text-red-500 font-light tracking-widest text-sm opacity-60">EINFACH ANRUFEN</span>
      </div>

      <div className="hidden lg:block absolute right-8 top-1/2 transform -translate-y-1/2 rotate-90 origin-right">
        <span className="text-red-500 font-light tracking-widest text-sm opacity-60">FRISCH ZUBEREITET</span>
      </div>
    </section>
  );
};

export default KebabContact;